import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ShieldCheck, ShieldAlert, X } from 'lucide-react';
import { toast } from 'sonner';
import { auth as authApi } from '../lib/api';
import { useAuth } from '../lib/auth';

const KIND_LABEL = {
  new_ip: 'New IP address',
  new_device: 'New browser / device',
  unusual_hour: 'Late-night sign-in',
};

/**
 * Shown at the very top of every logged-in page when a sign-in triggered
 * one or more anomaly signals (new IP, new device fingerprint, unusual hour).
 * Two acknowledgement paths: "That was me" (dismiss) or "That wasn't me"
 * (pings the admin sysupdates for investigation).
 */
export default function SecurityLoginAlertBanner() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);

  const load = useCallback(() => {
    if (!user) { setRows([]); return; }
    authApi.securityAlerts()
      .then(r => setRows(r.alerts || []))
      .catch(() => setRows([]));
  }, [user]);
  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  if (!user || rows.length === 0) return null;

  const ack = async (id, wasntMe) => {
    setRows(prev => prev.filter(r => r.id !== id));
    try {
      await authApi.ackSecurityAlert(id, wasntMe);
      if (wasntMe) toast.error('Reported — admin has been alerted.');
      else toast.success('Acknowledged. Stay safe.');
    } catch {
      toast.error('Could not update — refresh and try again.');
    }
  };

  return (
    <div data-testid="security-alert-banner" className="max-w-7xl mx-auto px-4 md:px-6 mt-3 space-y-2">
      {rows.slice(0, 2).map(r => (
        <div key={r.id} data-testid={`security-alert-${r.id}`}
          className="card-soft p-3 md:p-4 flex flex-wrap md:flex-nowrap items-center gap-3 border-l-4 border-[#C62828] bg-[#FFEBEE]/60">
          <div className="w-9 h-9 rounded-full bg-[#C62828]/15 text-[#C62828] grid place-items-center shrink-0">
            <ShieldAlert className="w-5 h-5"/>
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="font-heading font-bold text-sm">New sign-in detected — was this you?</div>
            <div className="text-xs text-ink/70 truncate">
              {(r.kinds || []).map(k => KIND_LABEL[k] || k).join(' · ')}
              {r.details?.new_ip && <span className="ml-2 font-mono">{r.details.new_ip}</span>}
              {r.details?.unusual_hour && <span className="ml-2">· {r.details.unusual_hour}</span>}
            </div>
            <div className="text-[10px] text-ink/50 uppercase tracking-wider mt-0.5">
              {new Date(r.at).toLocaleString()}
            </div>
          </div>
          <button data-testid={`security-alert-me-${r.id}`} onClick={() => ack(r.id, false)}
            className="pill !bg-white text-[#1B5E20] border-[#1B5E20]/30 hover:border-[#1B5E20] whitespace-nowrap">
            <ShieldCheck className="w-3 h-3"/>That was me
          </button>
          <button data-testid={`security-alert-not-me-${r.id}`} onClick={() => ack(r.id, true)}
            className="pill !bg-[#C62828] !text-white hover:bg-[#B71C1C] whitespace-nowrap">
            <AlertTriangle className="w-3 h-3"/>Not me
          </button>
          <button data-testid={`security-alert-dismiss-${r.id}`} onClick={() => ack(r.id, false)}
            className="text-ink/40 hover:text-ink/80 p-1" aria-label="Dismiss">
            <X className="w-4 h-4"/>
          </button>
        </div>
      ))}
    </div>
  );
}
